import { type Agency as UseUserAgency, useUser } from "@features/user"
import Button from "components/Button"
import Content from "components/Content"
import Loading from "components/Loading"
import MenuRadio from "components/MenuRadio"
import { saveAs } from "file-saver"
import useEnv from "hooks/useEnv"
import type React from "react"
import { useRef, useState } from "react"
import { Trans } from "react-i18next"
import signaturePlaceholder from "../../assets/images/signaturePlaceholder.jpg"
import WithPhoto from "./templates/WithPhoto"

interface User {
  first_name: string
  last_name: string | null
  email: string
  avatar?: string | null
}

interface TransformedUser {
  name: string
  agency: string
  role: string
  phoneNumber1: string
  phoneNumber2: string
  email: string
  website: string
  address: string
  facebook?: string
  instagram?: string
  linkedin: string
  documents: string
  photo: string
}

interface SizeOption {
  label: React.ReactNode
  value: boolean
}

function transformUser(
  user: User,
  agency: UseUserAgency,
  geconturUrl: string,
): TransformedUser {
  return {
    name: `${user.first_name}${user.last_name ? ` ${user.last_name}` : ""}`,
    agency: `${agency.social_denomination} | RNAVT ${agency.RNAVT}`,
    role: "Cargo",
    phoneNumber1: agency?.phone_number?.toString() || "",
    phoneNumber2: "210 196 239",
    email: user.email,
    website: `${agency.subdomain}.bestravel.pt`,
    address: `${agency.address}\n${agency.postal_code} ${agency.locale}`,
    facebook: agency.facebook_url,
    instagram: agency.instagram_url,
    linkedin: "https://www.linkedin.com/company/bestravel/",
    documents: "https://bestravel.pt/",
    photo: user?.avatar
      ? `${geconturUrl}/img/user-images/${user.avatar}`
      : (signaturePlaceholder as string) || "/default-avatar.jpg",
  }
}

const Signature: React.FC = () => {
  const { geconturUrl } = useEnv()
  const signatureRef = useRef<HTMLDivElement>(null)
  const { isLoading, data } = useUser()
  const [withPhoto, setWithPhoto] = useState<boolean>(false)

  const sizeOptions: SizeOption[] = [
    { label: <Trans i18nKey="signature.size.withPhoto" />, value: true },
    { label: <Trans i18nKey="signature.size.withoutPhoto" />, value: false },
  ]

  const copySignature = (): void => {
    const element = signatureRef.current
    if (!element) return

    const clonedElement = element.cloneNode(true) as HTMLElement

    // Remove contentEditable attribute from all elements within the cloned element
    const elements = clonedElement.querySelectorAll("[contenteditable]")

    for (let i = 0; i < elements.length; i++) {
      elements[i].removeAttribute("contenteditable")
    }

    // Get the HTML code of the modified element
    const html = clonedElement.innerHTML

    const modifiedHTML = html.replace(/contentEditable="[^"]*"/g, "")

    const blob = new Blob([modifiedHTML], {
      type: "text/plain;charset=utf-8",
    })
    saveAs(blob, "assinatura.htm")
  }

  return (
    <Content title={<Trans i18nKey="signature.title" />}>
      <div className="w-full h-full grid place-items-center p-2">
        {isLoading && <Loading className="mt-40" />}
        {!isLoading && data?.user && data?.agency && (
          <div className="text-center place-items-center grid p-12 rounded-xl">
            <div ref={signatureRef}>
              <WithPhoto
                user={transformUser(data.user, data.agency, geconturUrl || "")}
                withPhoto={withPhoto}
              />
            </div>
            <p style={{ marginTop: "15px" }}>
              <Trans i18nKey="signature.instructions.changePhotoPrefix" />{" "}
              <a
                style={{ color: "#3c8dbc", fontWeight: "bold" }}
                href={`${geconturUrl}`}
              >
                <Trans i18nKey="signature.instructions.home" />
              </a>
            </p>
            <div className="w-3/4 my-1 text-center flex flex-col gap-2 p-6 mx-auto">
              <MenuRadio
                selected={withPhoto}
                options={sizeOptions}
                onClick={setWithPhoto}
              />
            </div>
            <Button className="mt-4" primary medium onClick={copySignature}>
              <Trans i18nKey="signature.actions.download" />
            </Button>
          </div>
        )}
      </div>
    </Content>
  )
}

export default Signature
